import { useState } from 'react';
import { Icon } from '../../components/Icon';
import { Avatars, Chip, Ring } from '../../components/bits';
import { useStore } from '../../state/store';
import { dayVisits, progressOf, projectedEnd, type Pace } from '../../lib/derive';
import { fmtHour, miles } from '../../lib/time';
import type { GeoPoint } from '../../data/types';

const W = 760;
const H = 560;

/**
 * Vista “En vivo”. Mapa ILUSTRATIVO: en la integración se reemplaza por el proveedor de mapas
 * (rutas y tiempos de manejo reales). Reglas:
 *  - Línea sólida = recorrido con registros de inicio/fin. Punteada = lo que sigue (planeado).
 *  - La llegada solo se marca con el registro de inicio; routeReadiness no la confirma.
 *  - Quien no dio consentimiento de ubicación aparece como “ubicación desactivada”.
 */
export function LiveView() {
  const s = useStore();
  const snap = s.snapshot;
  const [focus, setFocus] = useState<string | null>(null);
  const pts = [...snap.clients.map(c => c.location), ...snap.teams.map(t => t.base)];
  const lat = [Math.min(...pts.map(p => p.lat)), Math.max(...pts.map(p => p.lat))];
  const lng = [Math.min(...pts.map(p => p.lng)), Math.max(...pts.map(p => p.lng))];
  const proj = (p: GeoPoint) => ({
    x: 60 + ((p.lng - lng[0]) / (lng[1] - lng[0])) * (W - 120),
    y: 50 + (1 - (p.lat - lat[0]) / (lat[1] - lat[0])) * (H - 100),
  });
  const hq = proj(snap.teams[0].base);

  return (
    <div className="row wrap" style={{ alignItems: 'flex-start', gap: 16 }}>
      <section className="card" aria-label="Mapa en vivo" style={{ flex: '999 1 600px', minWidth: 0, overflow: 'hidden' }}>
        <div className="row wrap" style={{ justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #F0EFEB' }}>
          <div className="row wrap" style={{ gap: 6 }}>
            <Chip bg="#DCFCE7" fg="#166534"><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16A34A' }} />En vivo · {fmtHour(snap.now)}</Chip>
            {snap.teams.map(t => (
              <button key={t.id} type="button" className={`pill${focus === t.id ? ' on' : ''}`} onClick={() => setFocus(focus === t.id ? null : t.id)}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: t.color }} />{t.name}
              </button>
            ))}
          </div>
          <span className="muted" style={{ fontSize: 12 }}>Ubicación solo en horario laboral · con consentimiento</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ position: 'relative', width: W, height: H, margin: '0 auto', background: '#EEF0EA' }}>
            <svg width={W} height={H} style={{ position: 'absolute', inset: 0 }} aria-hidden="true">
              {[80, 190, 300, 410, 500].map(y => <line key={y} x1={0} y1={y} x2={W} y2={y + 20} stroke="#fff" strokeWidth={y % 2 ? 6 : 10} />)}
              {[110, 250, 390, 530, 660].map(x => <line key={x} x1={x} y1={0} x2={x - 30} y2={H} stroke="#fff" strokeWidth={x % 3 ? 7 : 11} />)}
              {snap.teams.map(t => {
                const vs = dayVisits(snap, snap.today, t.id);
                const past = vs.filter(v => v.status !== 'scheduled');
                const next = vs.filter(v => v.status === 'scheduled');
                const dim = focus && focus !== t.id ? 0.15 : 1;
                const pastPts = [hq, ...past.map(v => proj(loc(v.clientUid)))];
                const nextPts = [pastPts[pastPts.length - 1], ...next.map(v => proj(loc(v.clientUid)))];
                return (
                  <g key={t.id} opacity={dim}>
                    <polyline points={pastPts.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke={t.color} strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
                    {next.length > 0 && <polyline points={nextPts.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke={t.color} strokeWidth={3.5} strokeDasharray="2 9" strokeLinecap="round" />}
                  </g>
                );
              })}
            </svg>
            <div style={{ position: 'absolute', left: hq.x, top: hq.y, transform: 'translate(-50%,-50%)', width: 30, height: 30, borderRadius: 8, background: '#2B2B2D', color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3 }}>HQ</div>
            {snap.teams.flatMap(t => dayVisits(snap, snap.today, t.id).map(v => {
              const p = proj(loc(v.clientUid));
              const pr = progressOf(v, snap.now);
              const dim = focus && focus !== t.id ? 0.15 : 1;
              const client = snap.clients.find(c => c.uid === v.clientUid);
              if (v.status === 'inProgress') {
                const warn = pr.pace === 'lento';
                return (
                  <div key={v.uid} style={{ position: 'absolute', left: p.x, top: p.y, transform: 'translate(-50%,-50%)', zIndex: 5, opacity: dim }}>
                    <Ring pct={pr.real ?? 0} color={t.color} size={46} thickness={5} fontSize={12} />
                    <div style={{ position: 'absolute', left: '50%', bottom: 52, transform: 'translateX(-50%)', background: '#fff', borderRadius: 10, padding: '5px 9px', fontSize: 12, whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,.18)', border: `2px solid ${warn ? '#E5484D' : t.color}` }}>
                      <b>{t.name} · {client?.name}</b><br />
                      <span style={{ color: warn ? '#B91C1C' : undefined, fontWeight: warn ? 700 : 400 }}>{pr.real}% real · {pr.expected}% esperado</span>
                    </div>
                  </div>
                );
              }
              const done = v.status === 'completed';
              return (
                <span key={v.uid} title={`${client?.name} · ${fmtHour(v.start)}`} style={{
                  position: 'absolute', left: p.x, top: p.y, transform: 'translate(-50%,-50%)', zIndex: 4, opacity: dim,
                  width: done ? 14 : 16, height: done ? 14 : 16, borderRadius: done ? '50%' : 4, background: done ? t.color : '#fff',
                  border: done ? '2px solid #fff' : `3px dashed ${v.risk ? '#E5484D' : t.color}`,
                }} />
              );
            }))}
            <span className="muted" style={{ position: 'absolute', left: 12, bottom: 10, fontSize: 11, fontWeight: 700 }}>Mapa ilustrativo</span>
          </div>
        </div>
        <div className="row wrap muted" style={{ gap: 14, padding: '10px 14px', borderTop: '1px solid #F0EFEB', fontSize: 12 }}>
          <span>━ Ruta recorrida</span><span>┅ Lo que sigue</span><span>◯ Avance en la casa (lista de tareas)</span><span>▢ Siguiente casa</span>
        </div>
      </section>

      <aside className="w" aria-label="Equipos ahora" style={{ flex: '1 1 340px', minWidth: 280, padding: 0, gap: 0 }}>
        <div style={{ padding: '14px 16px' }}><h3 style={{ margin: 0, fontSize: 16 }}>Equipos ahora</h3></div>
        {snap.teams.map(t => {
          const vs = dayVisits(snap, snap.today, t.id);
          const cur = vs.find(v => v.status === 'inProgress');
          const next = vs.find(v => v.status === 'scheduled');
          const pr = cur ? progressOf(cur, snap.now) : null;
          const end = cur ? projectedEnd(cur, snap.now) : null;
          const late = !!(end && next && end + 0.25 > next.start);
          const driven = vs.filter(v => v.status !== 'scheduled').reduce((acc, v, i, arr) =>
            acc + miles(i === 0 ? t.base : loc(arr[i - 1].clientUid), loc(v.clientUid)), 0);
          const noConsent = t.members.filter(m => !m.locationConsent);
          return (
            <div key={t.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 16px', borderTop: '1px solid #F0EFEB' }}>
              <div className="row"><span style={{ width: 12, height: 12, borderRadius: 4, background: t.color }} /><b>{t.name}</b><Avatars team={t} color={t.color} size={20} />
                <span style={{ marginLeft: 'auto' }}><PaceChip pace={pr?.pace ?? 'sin-dato'} driving={!cur} /></span></div>
              {cur && pr ? (
                <>
                  <span style={{ fontSize: 13 }}>En la casa desde {fmtHour(pr.startedAt!)} · <b>{snap.clients.find(c => c.uid === cur.clientUid)?.name}</b></span>
                  <div style={{ position: 'relative', height: 10, borderRadius: 99, background: '#F0EFEB' }}>
                    <i style={{ position: 'absolute', inset: '0 auto 0 0', width: `${pr.real}%`, background: t.color, borderRadius: 99 }} />
                    <b style={{ position: 'absolute', top: -4, bottom: -4, left: `${pr.expected}%`, width: 3, background: '#1F1F21', borderRadius: 2 }} aria-hidden="true" />
                  </div>
                  <span className="muted" style={{ fontSize: 12 }}>{pr.real}% · esperado {pr.expected}%{end ? ` · fin ~${fmtHour(end)}` : ''}</span>
                </>
              ) : (
                <span style={{ fontSize: 13 }}>{next ? <>Sigue: <b>{snap.clients.find(c => c.uid === next.clientUid)?.name}</b> · {fmtHour(next.start)} · llegada se confirma al iniciar</> : 'Sin más trabajos hoy'}</span>
              )}
              {late && next && <div style={{ background: '#FFF8EA', borderRadius: 8, padding: 8, fontSize: 12 }}>A este ritmo llega tarde a {snap.clients.find(c => c.uid === next.clientUid)?.name} ({fmtHour(next.start)}).</div>}
              <span className="muted" style={{ fontSize: 12 }}>Recorrido hoy ≈ {Math.round(driven * 10) / 10} mi</span>
              {noConsent.map(m => <span key={m.uid} style={{ fontSize: 12, color: '#5E5F63', border: '1.5px dashed #8A8B8E', borderRadius: 8, padding: '3px 8px', alignSelf: 'flex-start' }}>{m.name.split(' ')[0]}: ubicación desactivada</span>)}
            </div>
          );
        })}
      </aside>
    </div>
  );

  function loc(uid: string): GeoPoint { return snap.clients.find(c => c.uid === uid)?.location ?? snap.teams[0].base; }
}

function PaceChip({ pace, driving }: { pace: Pace; driving: boolean }) {
  if (driving) return <Chip bg="#F0EFEB" fg="#3A3A3D"><Icon name="car" color="#3A3A3D" size={13} />Entre casas</Chip>;
  const m: Record<Pace, [string, string, string]> = {
    'a-tiempo': ['#DCFCE7', '#166534', 'A tiempo'], lento: ['#FEE2E2', '#B91C1C', 'Va lento'],
    rapido: ['#DBEAFE', '#1D4ED8', 'Rápido'], 'sin-dato': ['#F0EFEB', '#3A3A3D', 'Sin dato'],
  };
  const [bg, fg, l] = m[pace];
  return <Chip bg={bg} fg={fg}>{l}</Chip>;
}
